import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchScreener } from '../FunViz/data/dataService';

const DEFAULT_SIZE = 25;
const DEFAULT_SORT = { key: 'mcap', dir: 'desc' };

// Snapshot metric keys that take a min/max range filter. Each key becomes
// two filter-state fields (`<key>Min`/`<key>Max`) and two query params
// (`f.<key>.min`/`f.<key>.max`), exactly the pattern the original four
// filters used. The filter UI's labels/steps live with the component
// (Screener.js FILTER_DEFS); this list is the single source for the params.
export const RANGE_FILTER_KEYS = [
  'divYield',
  'epsStab',
  'payoffYears',
  'volume',
  'peBlended',
  'evEbitda',
  'roic',
  'roe',
  'netDebtEbitda',
  'interestCoverage',
  'divCagr5',
  'divStreakYears',
  'payoutFcf',
  'qualityScore',
  'growthScoreTotal',
];

const DEFAULT_FILTERS = {
  ...Object.fromEntries(RANGE_FILTER_KEYS.flatMap((key) => [[`${key}Min`, ''], [`${key}Max`, '']])),
  sector: '',
};

const DEFAULT_RESULT = { total: 0, totalPages: 0, asOf: null, rows: [] };

/**
 * Manages the Screener tab's filters, sort, pagination and fetch status
 * against `GET ${REACT_APP_BACKEND_URL}/screener` (see
 * `dataService.fetchScreener`, which this hook is the only caller of).
 *
 * `status` is one of:
 *   - `'idle'`: before the first fetch has resolved.
 *   - `'fetching'`: a request is in flight (initial load, or any
 *     filter/sort/page change).
 *   - `'fetched'`: the last request succeeded — `rows`/`total`/`totalPages`/
 *     `asOf` are current.
 *   - `'unavailable'`: the last request got HTTP 503 (no snapshot computed
 *     yet). Kept distinct from `'error'` so the Screener component can
 *     render "Screener data is still being prepared" instead of a generic
 *     failure message — see `fetchScreener`'s `status: 'unavailable'`.
 *   - `'error'`: the last request failed for any other reason (a non-ok,
 *     non-503 response, or a network failure).
 *
 * Changing a filter, the sector or the sort column/direction resets to
 * page 1, since the previous page number may not exist in the new result
 * set. Only the page number itself does not reset anything.
 */
export const useScreener = () => {
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [sortState, setSortState] = useState(DEFAULT_SORT);
  const [page, setPage] = useState(1);
  const [size] = useState(DEFAULT_SIZE);
  const [status, setStatus] = useState('idle');
  const [result, setResult] = useState(DEFAULT_RESULT);

  // Guards against an in-flight request from a since-superseded
  // filter/sort/page change resolving after a newer one and clobbering
  // state with stale results.
  const requestIdRef = useRef(0);

  const runFetch = useCallback(async (nextFilters, nextSortState, nextPage, nextSize) => {
    setStatus('fetching');
    const requestId = ++requestIdRef.current;

    const params = {
      page: nextPage,
      size: nextSize,
      sort: nextSortState.key,
      dir: nextSortState.dir,
      ...Object.fromEntries(RANGE_FILTER_KEYS.flatMap((key) => [
        [`f.${key}.min`, nextFilters[`${key}Min`]],
        [`f.${key}.max`, nextFilters[`${key}Max`]],
      ])),
      'f.sector': nextFilters.sector,
    };

    const response = await fetchScreener(params);

    if (requestId !== requestIdRef.current) return;

    setStatus(response.status === 'ok' ? 'fetched' : response.status);
    setResult({
      total: response.total,
      totalPages: response.totalPages,
      asOf: response.asOf,
      rows: response.rows,
    });
  }, []);

  // Initial fetch, and refetch whenever filters/sort/page/size change.
  useEffect(() => {
    runFetch(filters, sortState, page, size);
  }, [filters, sortState, page, size, runFetch]);

  const updateFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  }, []);

  const toggleSort = useCallback((key) => {
    setPage(1);
    setSortState((prev) => (
      prev.key === key
        ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' }
        : { key, dir: 'desc' }
    ));
  }, []);

  const goToPage = useCallback((nextPage) => {
    setPage(nextPage);
  }, []);

  return {
    filters,
    updateFilter,
    sort: sortState.key,
    dir: sortState.dir,
    toggleSort,
    page,
    size,
    goToPage,
    status,
    total: result.total,
    totalPages: result.totalPages,
    asOf: result.asOf,
    rows: result.rows,
  };
};
